import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalizedLoaded,
  type RouteLocationNormalized,
  type NavigationGuardNext,
  type Router,
  RouteLocationRaw,
} from 'vue-router';

import { userDetailsStore } from '@/stores/auth/user-details.store';

import authHelper from '../shared/helpers/auth/auth.helper';

import authRoutes from './auth';
import mainRoutes from './main';

import type { ModuleLink, RouteRecordRaw } from '@bv/router';

const routes: RouteRecordRaw[] = [mainRoutes, authRoutes];

export const modules: ModuleLink[] = [
  {
    title: 'Workspaces & User management',
    route: { name: 'mng' },
    icon: 'bvi-briefcase',
    visible: true,
  },
  {
    route: { name: 'cc' },
    title: 'Call center',
    icon: 'bvi-service-call-center',
    visible: true,
  },
  {
    title: 'Cloud Lines',
    route: { name: 'cl' },
    icon: 'bvi-service-cloud-lines',
    visible: true,
  },
  {
    title: 'Business chat',
    route: { name: 'bc' },
    icon: 'bvi-service-chat',
    visible: true,
  },
  {
    title: 'SMS',
    route: { name: 'sms' },
    icon: 'bvi-service-sms',
    visible: true,
  },
  {
    title: 'Settings',
    route: { name: 's' },
    icon: 'bvi-setting',
    visible: true,
  },
];

export const mainRoute: RouteRecordRaw = {
  path: '/',
  name: 'main',
  redirect: { name: 'mng' },
  children: [mainRoutes, authRoutes],
};

const router: Router = createRouter({
  routes,
  history: createWebHistory(),
});

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload CSS')
  ) {
    window.location.href = to.fullPath;
  }
});

const routePermissions = (route: RouteLocationNormalized): string[] => {
  const permissions: string[] = [];

  route.matched.forEach((r) => {
    if (Array.isArray(r.meta.permissions) || typeof r.meta.permissions === 'string') {
      const routePermissions = Array.isArray(r.meta.permissions) ? r.meta.permissions : [r.meta.permissions];

      permissions.push(...routePermissions);
    }
  });

  return permissions;
};

router.beforeEach(
  async (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded, next: NavigationGuardNext) => {
    const isAuth = await authHelper.checkAuth();

    if (to.meta.public) {
      next();
    } else if (
      !isAuth &&
      (to.name == 'auth-login' ||
        to.name == 'auth-forget-password' ||
        to.name == 'auth-otp-verify' ||
        to.name == 'auth-otp-reset' ||
        to.name == 'auth-invitation' ||
        to.name == 'auth-reset-password')
    ) {
      next();
    } else if (
      isAuth &&
      (to.name == 'auth-login' ||
        to.name == 'auth-forget-password' ||
        to.name == 'auth-otp-verify' ||
        to.name == 'auth-otp-reset' ||
        to.name == 'auth-invitation' ||
        to.name == 'auth-reset-password')
    ) {
      next({ name: 'home' });
    } else if (!isAuth) {
      next({ name: 'auth-login' });
    } else {
      const userStore = userDetailsStore();

      const permissions: string[] = routePermissions(to);
      if (!(await userStore.hasPermissions(permissions))) {
        const redirectRoute: RouteLocationRaw | undefined =
          to.meta.redirectUnauthorized !== to.name ? (to.meta.redirectUnauthorized as RouteLocationRaw) : undefined;

        next(redirectRoute || { name: 'home' });
      } else {
        next();
      }
    }
  },
);

export default router;
